import axios, { AxiosError } from "axios";
import { getDynamicTranslation } from "../components/core/localization/localizationUtils";

/**
 * Represents common backend error payload fields returned by API requests.
 */
interface ApiErrorResponse {
  detail?: string;
  message?: string;
  [key: string]: unknown;
}

/**
 * Checks whether the given payload can be treated as an API error object.
 * @param value The unknown payload to validate.
 * @returns True when the payload is a non-null object.
 */
const isApiErrorResponse = (value: unknown): value is ApiErrorResponse => {
  return typeof value === "object" && value !== null;
};

/**
 * Extracts a translated error message from a raw API error payload.
 * Supports object-shaped validation payloads as well as plain string messages.
 * @param payload The raw API error payload.
 * @param fallback The fallback message used when no payload message can be derived.
 * @returns A translated error message string.
 */
export const getApiErrorMessageFromPayload = (
  payload: unknown,
  fallback = ""
): string => {
  if (isApiErrorResponse(payload)) {
    const translatedMessages = Object.values(payload)
      .map((value) => getDynamicTranslation(value, "snackbars"))
      .filter((value) => typeof value === "string" && value !== "");

    if (translatedMessages.length > 0) {
      return translatedMessages.join("");
    }
  }

  if (typeof payload === "string" && payload !== "") {
    return getDynamicTranslation(payload, "snackbars");
  }

  return fallback;
};

/**
 * Extracts a translated error message from an Axios error response.
 * Falls back to the provided message when the response payload contains no usable error text.
 * @param error The thrown Axios error or unknown runtime value.
 * @param fallback The fallback message used when no payload message can be derived.
 * @returns A translated error message string.
 */
export const getAxiosErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }

  const axiosError = error as AxiosError<ApiErrorResponse>;
  return getApiErrorMessageFromPayload(axiosError.response?.data, fallback);
};
